"use client";

import { useState, useRef, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { ChevronLeft, ChevronRight, ArrowRight, ShieldCheck, Users, Clock } from "lucide-react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";

const SLIDES = [
    {
        id: 1,
        subtitle: "Get Quality Service & Solutions",
        title: "Reliable IT Solutions for a Smarter Tomorrow",
        desc: "Making a decision to do something is the first step. Nothing moves until someone decides to take action.",
        image: "/images/hero/slide-1.webp",
        cta: "Get started now",
        layout: "center",
        animation: "fadeUp",
    },
    {
        id: 2,
        subtitle: "Innovation & Technology",
        title: "Empowering Business through Digital Innovation",
        desc: "Transform your business operations with cutting-edge technology strategies designed for growth and efficiency.",
        image: "/images/hero/slide-2.webp",
        cta: "Discover More",
        layout: "left",
        animation: "zoomIn",
    },
    {
        id: 3,
        subtitle: "Secure Infrastructure",
        title: "Secure & Scalable Network Systems",
        desc: "Building the backbone of your digital enterprise with robust security and high-performance infrastructure.",
        image: "/images/hero/slide-3.webp",
        cta: "Our Services",
        layout: "left",
        animation: "slideRight",
    },
];

const ANIMATION_CONFIGS = {
    fadeUp: {
        from: { y: 30, opacity: 0, filter: 'blur(0px)' },
        to: { y: 0, opacity: 1, filter: 'blur(0px)', duration: 0.8, ease: "power2.out" }
    },
    zoomIn: {
        from: { scale: 0.9, opacity: 0, filter: 'blur(10px)' },
        to: { scale: 1, opacity: 1, filter: 'blur(0px)', duration: 1.2, ease: "power3.out" }
    },
    slideRight: {
        from: { x: -50, opacity: 0, filter: 'blur(0px)' },
        to: { x: 0, opacity: 1, filter: 'blur(0px)', duration: 0.8, ease: "power2.out" }
    }
};

export function HeroSlider() {
    const [current, setCurrent] = useState(0);
    const containerRef = useRef<HTMLDivElement>(null);
    const slideRefs = useRef<(HTMLDivElement | null)[]>([]);
    const tlRef = useRef<gsap.core.Timeline | null>(null);


    const hasAnimatedRef = useRef(false);

    useGSAP(() => {
        if (tlRef.current) tlRef.current.kill();

        const currentSlideEl = slideRefs.current[current];
        const prevSlidesEls = slideRefs.current.filter((_, i) => i !== current);

        if (!currentSlideEl) return;

        const tl = gsap.timeline({ defaults: { ease: "power3.out" } });
        tlRef.current = tl;

        gsap.set(currentSlideEl, { zIndex: 10 });
        gsap.set(prevSlidesEls, { zIndex: 0 });

        const slideData = SLIDES[current];
        const animType = slideData.animation as keyof typeof ANIMATION_CONFIGS;
        const config = ANIMATION_CONFIGS[animType] || ANIMATION_CONFIGS.fadeUp;

        const texts = currentSlideEl.querySelectorAll(".animate-text");
        const img = currentSlideEl.querySelector(".hero-image");

        // First paint: keep slide 0 fully visible for LCP/Speed Index (no fade-from-zero).
        if (!hasAnimatedRef.current && current === 0) {
            hasAnimatedRef.current = true;
            gsap.set(currentSlideEl, { opacity: 1 });
            gsap.set(texts, { clearProps: "all" });
            if (img) gsap.to(img, { scale: 1.1, duration: 5, ease: "none" });
            return;
        }

        hasAnimatedRef.current = true;
        gsap.set(texts, config.from);
        gsap.set(currentSlideEl, { opacity: 0 });

        tl.to(currentSlideEl, { opacity: 1, duration: 0.5 })
            .to(prevSlidesEls, { opacity: 0, duration: 0.5 }, "<")
            .to(img, { scale: 1.1, duration: 5, ease: "none" }, "<")
            .to(texts, { ...config.to, stagger: 0.1 }, "<0.3");
    }, { scope: containerRef, dependencies: [current] });

    useEffect(() => {
        const timer = setInterval(() => {
            setCurrent((prev) => (prev === SLIDES.length - 1 ? 0 : prev + 1));
        }, 6000);
        return () => clearInterval(timer);
    }, []);

    const nextSlide = () => {
        setCurrent((prev) => (prev === SLIDES.length - 1 ? 0 : prev + 1));
    };

    const prevSlide = () => {
        setCurrent((prev) => (prev === 0 ? SLIDES.length - 1 : prev - 1));
    };

    return (
        <section
            ref={containerRef}
            className="relative bg-black text-white group/slider h-[560px] md:h-[640px] lg:h-[620px] xl:h-[700px] 2xl:h-[min(760px,calc(100vh-140px))]"
        >
            <div className="absolute inset-0 w-full h-full overflow-hidden">
                {SLIDES.map((slide, index) => (
                    <div
                        key={slide.id}
                        ref={el => { slideRefs.current[index] = el }}
                        className={`slide-container absolute inset-0 h-full w-full ${index === 0 ? "opacity-100" : "opacity-0"}`}
                    >
                        <div className="absolute inset-0 z-0">
                            <div className="relative w-full h-full">
                                <Image
                                    src={slide.image}
                                    alt={slide.title}
                                    fill
                                    className="object-cover hero-image"
                                    priority={index === 0}
                                    fetchPriority={index === 0 ? "high" : "low"}
                                    sizes="100vw"
                                    quality={index === 0 ? 70 : 60}
                                    loading={index === 0 ? "eager" : "lazy"}
                                />
                                <div className={`absolute inset-0 ${slide.layout === 'center'
                                    ? 'bg-[#001746]/70'
                                    : 'bg-gradient-to-r from-[#001746]/90 via-[#001746]/60 to-transparent'
                                    }`} />
                            </div>
                        </div>

                        {/* Extra bottom space on lg+ so copy clears the overlapping info strip */}
                        <div className="container mx-auto relative z-10 h-full px-4 pb-16 pt-10 lg:pb-36 xl:pb-40">
                            <div className={`flex h-full flex-col justify-center ${slide.layout === 'center'
                                ? 'mx-auto max-w-4xl items-center text-center'
                                : 'max-w-2xl items-start text-left xl:max-w-3xl'
                                }`}>
                                <div className="mb-4 md:mb-5">
                                    <div className="animate-text">
                                        <span className="inline-block border border-white/30 bg-white/10 px-3 py-1 text-xs font-medium uppercase tracking-wider backdrop-blur-sm md:text-sm">
                                            {slide.subtitle}
                                        </span>
                                    </div>
                                </div>

                                <div className="mb-4 md:mb-5">
                                    {index === 0 ? (
                                        <h1 className="animate-text text-3xl font-bold leading-[1.15] tracking-tight md:text-5xl lg:text-[44px] xl:text-5xl 2xl:text-6xl">
                                            {slide.title}
                                        </h1>
                                    ) : (
                                        <h2 className="animate-text text-3xl font-bold leading-[1.15] tracking-tight md:text-5xl lg:text-[44px] xl:text-5xl 2xl:text-6xl">
                                            {slide.title}
                                        </h2>
                                    )}
                                </div>

                                <div className="mb-6 md:mb-8">
                                    <p className="animate-text max-w-2xl text-base font-light leading-relaxed text-gray-200 md:text-lg">
                                        {slide.desc}
                                    </p>
                                </div>

                                <div className="animate-text flex flex-wrap gap-3 md:gap-4">
                                    <Link
                                        href="/contact"
                                        className="group flex items-center gap-2 bg-primary px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-white hover:text-primary md:px-8 md:py-3.5 md:text-base"
                                    >
                                        {slide.cta}
                                        <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                                    </Link>
                                    <Link
                                        href="/services"
                                        className="border border-white px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-white hover:text-secondary md:px-8 md:py-3.5 md:text-base"
                                    >
                                        View Services
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}

                <button
                    onClick={prevSlide}
                    className="absolute left-4 top-1/2 z-20 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 bg-white/10 text-white opacity-0 backdrop-blur-sm transition-all duration-300 group-hover/slider:opacity-100 hover:border-primary hover:bg-primary md:left-8 md:h-10 md:w-10"
                    aria-label="Previous slide"
                >
                    <ChevronLeft className="h-6 w-6" />
                </button>
                <button
                    onClick={nextSlide}
                    className="absolute right-4 top-1/2 z-20 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 bg-white/10 text-white opacity-0 backdrop-blur-sm transition-all duration-300 group-hover/slider:opacity-100 hover:border-primary hover:bg-primary md:right-8 md:h-10 md:w-10"
                    aria-label="Next slide"
                >
                    <ChevronRight className="h-6 w-6" />
                </button>
            </div>

            {/* Info strip — only overlays on wide screens; sits under content padding */}
            <div className="pointer-events-none absolute inset-x-0 bottom-0 z-30 hidden lg:block">
                <div className="container mx-auto px-4">
                    <div className="pointer-events-auto grid grid-cols-3 bg-white text-[#001746] shadow-lg">
                        <div className="flex items-center gap-3 border-r border-gray-200 px-4 py-5 xl:gap-4 xl:px-6 xl:py-6">
                            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/15 xl:h-12 xl:w-12">
                                <ShieldCheck className="h-5 w-5 text-primary xl:h-6 xl:w-6" />
                            </div>
                            <div className="min-w-0">
                                <p className="truncate text-sm font-bold xl:text-base">Trusted Services</p>
                                <p className="truncate text-xs text-gray-600 xl:text-sm">Reliable IT Solutions</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3 border-r border-gray-200 px-4 py-5 xl:gap-4 xl:px-6 xl:py-6">
                            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/15 xl:h-12 xl:w-12">
                                <Users className="h-5 w-5 text-primary xl:h-6 xl:w-6" />
                            </div>
                            <div className="min-w-0">
                                <p className="truncate text-sm font-bold xl:text-base">Expert Team</p>
                                <p className="truncate text-xs text-gray-600 xl:text-sm">Dedicated Professionals</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3 px-4 py-5 xl:gap-4 xl:px-6 xl:py-6">
                            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/15 xl:h-12 xl:w-12">
                                <Clock className="h-5 w-5 text-primary xl:h-6 xl:w-6" />
                            </div>
                            <div className="min-w-0">
                                <p className="truncate text-sm font-bold xl:text-base">Professional Support</p>
                                <p className="truncate text-xs text-gray-600 xl:text-sm">Always Available</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
